import { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { Alert, Button, IconButton, Snackbar, Typography } from "@mui/material";
import {
  CloseOutlined as CloseOutlinedIcon,
  InstallDesktopOutlined as InstallDesktopOutlinedIcon,
  InstallMobileOutlined as InstallMobileOutlinedIcon,
} from "@mui/icons-material";
import { SHOW_INSTALLATION_ALERT, SHOW_IOS_INSTALLATION_GUIDE, WELCOME_PAGE_VISITED } from "../../modules/configuration";
import { INSTALLA_APP_BUTTON, INSTALLA_APP_INFO, INSTALLA_APP_REQUEST, NO_LABEL, YES_LABEL } from "../../modules/language";
import { InstallationDrawer } from "./InstallationDrawer";
import { getMessageString } from "../../utility";

const IPHONE = "iPhone";
const IPAD = "iPad";
const MAC = "Macintosh";
const ANDROID = "Android";
const WINDOWS = "Windows";

// User agent Browser Desktop
const CHROME = "Chrome";
const FIREFOX = "Firefox";
const DUCK_DUCK_GO = "Ddg";
const SAFARI = "Safari";
const OPERA = "OPR";
const EDGE = "Edg";

// User agent Browser Mobile
const CHROME_MOBILE = "CriOS";
const FIREFOX_MOBILE = "FxiOS";
const DUCK_DUCK_GO_MOBILE = "Ddg";
const OPERA_MOBILE = "OPT";
const EDGE_MOBILE = "EdgiOS";
const GOOGLE_MOBILE = "GSA";

const detectDevice = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes(IPHONE)) return "iPhone";
  else if (userAgent.includes(IPAD)) return "iPad";
  else if (userAgent.includes(MAC)) return "Mac";
  else if (userAgent.includes(ANDROID)) return "Android";
  else if (userAgent.includes(WINDOWS)) return "Windows";
};

const detectBrowser = () => {
  const userAgent = navigator.userAgent;
  // Navigo da iOS
  if ([IPHONE, IPAD].some((val) => userAgent.includes(val))) {
    // prettier-ignore
    if (userAgent.includes(SAFARI) && ![CHROME_MOBILE, FIREFOX_MOBILE, DUCK_DUCK_GO_MOBILE, OPERA_MOBILE, EDGE_MOBILE, GOOGLE_MOBILE].some((val) => userAgent.includes(val))) return "Safari";
    if (userAgent.includes(CHROME_MOBILE)) return "Chrome";
    else if (userAgent.includes(FIREFOX_MOBILE)) return "Firefox";
    else if (userAgent.includes(DUCK_DUCK_GO_MOBILE)) return "Duck Duck Go";
    else if (userAgent.includes(OPERA_MOBILE)) return "Opera";
    else if (userAgent.includes(EDGE_MOBILE)) return "Edge";
    else if (userAgent.includes(GOOGLE_MOBILE)) return "Google";
  } else {
    // Navigo da desktop
    if (userAgent.includes(SAFARI) && ![CHROME, FIREFOX, DUCK_DUCK_GO, OPERA, EDGE].some((val) => userAgent.includes(val))) return "Safari";
    if (userAgent.includes(FIREFOX)) return "Firefox";
    if (userAgent.includes(OPERA)) return "Opera";
    if (userAgent.includes(DUCK_DUCK_GO)) return "Duck Duck Go";
  }
};

const GuidaInstallazione = injectIntl(({ intl }) => {
  const [isInstallAlertOpen, setIsInstallAlertOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosInstallationGuide, setShowIosInstallationGuide] = useState(localStorage.getItem(SHOW_IOS_INSTALLATION_GUIDE) == null);
  const [showInstallationDrawer, setShowInstallationDrawer] = useState(false);
  const [pwaInstalled, setPwaInstalled] = useState(navigator.standalone);

  const userAgent = navigator.userAgent;
  const isIosDevice = !("Notification" in window);
  const isMacDevice = userAgent.includes(MAC);
  const isMobile = [IPHONE, IPAD].some((val) => userAgent.includes(val));

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", (e) => {
      // Impedisco il lancio automatico del prompt
      e.preventDefault();
      // Setto l'evento di installazione alla variabile di stato affinchè sia triggerabile in un secondo momento
      setDeferredPrompt(e);
      if (localStorage.getItem(SHOW_INSTALLATION_ALERT) !== "false") setIsInstallAlertOpen(true);
    });

    window.addEventListener("appinstalled", () => {
      // Nascondo la visualizzazione del banner d'installazione se l'app è già stata installata
      setIsInstallAlertOpen(false);
      // Azzero la variabile di stato affinchè non sia più triggerabile in un secondo momento
      setDeferredPrompt(null);
      // Setto a true la variabile per informare che l'app è installata
      setPwaInstalled(true);
    });

    window.addEventListener("storage", () => {
      if (localStorage.getItem(SHOW_IOS_INSTALLATION_GUIDE) == null) setShowIosInstallationGuide(true);
    });
  }, []);

  const pwaSupported = () => {
    // prettier-ignore
    if (isIosDevice && [SAFARI, CHROME_MOBILE, FIREFOX_MOBILE, DUCK_DUCK_GO_MOBILE, EDGE_MOBILE].some((val) => userAgent.includes(val)) && ![OPERA_MOBILE, GOOGLE_MOBILE].some((val) => userAgent.includes(val))) return true;
    else if (isMacDevice && userAgent.includes(SAFARI) && ![CHROME, FIREFOX, DUCK_DUCK_GO].some((val) => userAgent.includes(val))) return true;
    else return false;
  };

  const installApp = async () => {
    // Nascondo la visualizzazione del banner d'installazione
    setIsInstallAlertOpen(false);
    // Mostro il prompt d'installazione
    deferredPrompt.prompt();
    // Azzero la variabile di stato affinchè non sia più triggerabile in un secondo momento
    setDeferredPrompt(null);
  };

  return (
    localStorage.getItem(WELCOME_PAGE_VISITED) != null && (
      <>
        {!pwaInstalled && pwaSupported() && showIosInstallationGuide && (
          <Alert
            variant="outlined"
            severity="info"
            action={
              <>
                <Button
                  startIcon={isMobile ? <InstallMobileOutlinedIcon /> : <InstallDesktopOutlinedIcon />}
                  onClick={() => setShowInstallationDrawer(true)}
                >
                  {getMessageString(intl, INSTALLA_APP_BUTTON)}
                </Button>
                <IconButton
                  onClick={() => {
                    localStorage.setItem(SHOW_IOS_INSTALLATION_GUIDE, false);
                    setShowIosInstallationGuide(false);
                  }}
                >
                  <CloseOutlinedIcon />
                </IconButton>
              </>
            }
          >
            <Typography>{getMessageString(intl, INSTALLA_APP_INFO)}</Typography>
          </Alert>
        )}
        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          open={isInstallAlertOpen}
          onClose={() => setIsInstallAlertOpen(false)}
          message={getMessageString(intl, INSTALLA_APP_REQUEST)}
          action={
            <>
              <Button color="inherit" size="small" onClick={() => installApp()}>
                {getMessageString(intl, YES_LABEL)}
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setIsInstallAlertOpen(false);
                  localStorage.setItem(SHOW_INSTALLATION_ALERT, false);
                }}
              >
                {getMessageString(intl, NO_LABEL)}
              </Button>
            </>
          }
        />
        <InstallationDrawer open={showInstallationDrawer} onClose={() => setShowInstallationDrawer(false)} />
      </>
    )
  );
});

export {
  IPHONE,
  IPAD,
  MAC,
  ANDROID,
  WINDOWS,
  CHROME,
  FIREFOX,
  DUCK_DUCK_GO,
  SAFARI,
  OPERA,
  EDGE,
  CHROME_MOBILE,
  FIREFOX_MOBILE,
  DUCK_DUCK_GO_MOBILE,
  OPERA_MOBILE,
  EDGE_MOBILE,
  GOOGLE_MOBILE,
  detectDevice,
  detectBrowser,
  GuidaInstallazione,
};
